import {
  DEFAULT_REPORT_PERIOD,
  REPORT_CHART_CATALOG_TO_KEY,
  SHARMA_REPORT_PATHS,
  TABLE_CATALOG_TO_REPORT_KEY,
  YTD_PERFORMANCE_BY_INDEX_DISPLAY_TITLE,
} from "../config/sharmaReportsConfig";
import { store } from "../redux/store";
import { selectActivePortfolioDisplayName } from "../redux/slices/portfolioSlice";
import {
  adaptDrawdownsChart,
  adaptMarginalRiskBetaChart,
  adaptRiskAttributionChart,
  adaptRiskProfileRadarChart,
  adaptTableReport,
} from "./sharmaReportAdapters";
import { enrichSharmaPayload, fetchSharmaReportJson } from "./sharmaReportsApi";
import { isValidReportPeriodYyyyMm } from "./sharmaReportValidation";

export function resolveSharmaFetchPeriod() {
  try {
    const cd = store.getState()?.portfolio?.contextData;
    const raw = cd?.reportPeriod ?? cd?.perd;
    if (typeof raw === "string" && isValidReportPeriodYyyyMm(raw)) {
      return raw.trim();
    }
  } catch {
    /* p.ej. tests sin Provider */
  }
  return DEFAULT_REPORT_PERIOD;
}

/**
 * @param {string} tableCatalogKey - key from SHARMA_TABLE_CATALOG_KEYS / TABLE_CATALOG_TO_REPORT_KEY
 * @param {string} portfolioApiId - resolved Sharma `portfolio_id` (e.g. DSPRIVEQ, DSINTEQ)
 * @returns {Promise<object>|null} adapted table or null if not a Sharma report table
 */
export async function loadSharmaTableByCatalogKey(
  tableCatalogKey,
  portfolioApiId,
  signal
) {
  const reportKey = TABLE_CATALOG_TO_REPORT_KEY[tableCatalogKey];
  if (!reportKey) {
    return null;
  }
  let path;
  if (reportKey === "ytdByStrategy") {
    path = SHARMA_REPORT_PATHS.ytdPerformanceByStrategy;
  } else if (reportKey === "ytdByIndex") {
    path = SHARMA_REPORT_PATHS.ytdPerformanceByIndex;
  } else if (reportKey === "valueAtRisk") {
    path = SHARMA_REPORT_PATHS.valueAtRisk;
  } else if (reportKey === "mcValueAtRisk") {
    path = SHARMA_REPORT_PATHS.mcValueAtRisk;
  } else {
    return null;
  }
  const period = resolveSharmaFetchPeriod();
  const json = await fetchSharmaReportJson(path, {
    portfolioApiId,
    period,
    signal,
  });
  const ctx = {
    perd: period,
    defaultTitle:
      reportKey === "ytdByIndex"
        ? YTD_PERFORMANCE_BY_INDEX_DISPLAY_TITLE
        : tableCatalogKey,
  };
  return enrichSharmaPayload(adaptTableReport(reportKey, json, ctx), "table");
}

export async function loadSharmaBusinessCycleScenario(
  portfolioApiId,
  window,
  signal
) {
  const period = resolveSharmaFetchPeriod();
  const json = await fetchSharmaReportJson(
    SHARMA_REPORT_PATHS.businessCycleScenario,
    {
      portfolioApiId,
      period,
      signal,
      extraQuery: {
        window,
        mode: "portfolio",
        method: "nonlinear",
      },
    }
  );
  const ctx = {
    perd: period,
    defaultTitle: "Business Cycle Scenario",
  };
  return enrichSharmaPayload(
    adaptTableReport("businessCycleScenario", json, ctx),
    "table"
  );
}

/**
 * @param {string} chartCatalogKey - key from REPORT_CHART_NAMES
 * @param {string} portfolioApiId - resolved Sharma `portfolio_id` (e.g. DSPRIVEQ, DSINTEQ)
 * @returns {Promise<object>}
 */
export async function loadSharmaChartByCatalogKey(
  chartCatalogKey,
  portfolioApiId,
  signal
) {
  const rk = REPORT_CHART_CATALOG_TO_KEY[chartCatalogKey];
  if (!rk) {
    return null;
  }
  let path;
  if (rk === "drawdownsOverTime") {
    path = SHARMA_REPORT_PATHS.drawdownsOverTime;
  } else if (rk === "riskAttribution") {
    path = SHARMA_REPORT_PATHS.riskAttribution;
  } else if (rk === "marginalRiskBeta") {
    path = SHARMA_REPORT_PATHS.marginalRiskBeta;
  } else if (rk === "riskProfile") {
    path = SHARMA_REPORT_PATHS.riskProfile;
  } else {
    return null;
  }
  const period = resolveSharmaFetchPeriod();
  const json = await fetchSharmaReportJson(path, {
    portfolioApiId,
    period,
    signal,
  });
  const withCatalog = (payload) =>
    payload && typeof payload === "object"
      ? { ...payload, reportChartCatalogKey: chartCatalogKey }
      : payload;

  let payload;
  if (rk === "drawdownsOverTime") {
    payload = adaptDrawdownsChart(json, { defaultTitle: chartCatalogKey });
  } else if (rk === "marginalRiskBeta") {
    payload = adaptMarginalRiskBetaChart(json, {
      defaultTitle: chartCatalogKey,
      perd: period,
      portfolioDisplayName: selectActivePortfolioDisplayName(store.getState()),
    });
  } else if (rk === "riskProfile") {
    payload = adaptRiskProfileRadarChart(json, {
      defaultTitle: chartCatalogKey,
      perd: period,
    });
  } else {
    payload = adaptRiskAttributionChart(json, { defaultTitle: chartCatalogKey });
  }
  return withCatalog(enrichSharmaPayload(payload, "chart"));
}
